/**
 * The hub's beacon: while its card is open, a big QR code of the world's link floats over the pad,
 * with the world's code under it, for anyone's camera to read off the screen. A DOM element moved
 * with the planet like the name tags; it takes no touches, so the planet under it still turns.
 */
import { KIND } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { qrDrawing } from '@core/qr';
import type { Invite } from '@game/session/invite';

import type { Planet } from '../planet/planet';
import { onPlanet, type PlanetPose } from '../planet/pose';
import type { PlanetView } from '../planet/view';
import { screenPoint, type Projector } from './projector';
import './beacon.css';

/** How high over the pad the beacon hangs: above the head of whoever stands there. */
const LIFT = 1.1;
/** White squares around the code, besides the card's own white edge: scanners need a margin. */
const QUIET = 2;
const SVG = 'http://www.w3.org/2000/svg';

export interface HubBeacon {
  /** Over the hub while there's an `invite` (its card is open) and the camera sees the pad. */
  place(shown: PlanetView | null, pose: PlanetPose, invite: Invite | null): void;
  remove(): void;
}

export function createHubBeacon(
  root: HTMLElement,
  projector: Projector,
  planet: Planet,
): HubBeacon {
  const beacon = document.createElement('div');
  beacon.className = 'hub-beacon';
  beacon.hidden = true;
  const card = document.createElement('div');
  card.className = 'card';
  const svg = document.createElementNS(SVG, 'svg');
  const path = document.createElementNS(SVG, 'path');
  svg.append(path);
  const code = document.createElement('b');
  code.className = 'code';
  card.append(svg, code);
  const beam = document.createElement('i');
  beam.className = 'beam';
  beacon.append(card, beam);
  root.append(beacon);

  const dir = new THREE.Vector3();
  const point = new THREE.Vector3();
  const screen = screenPoint();
  let drawn = '';
  let at = '';

  /** The QR code of the invite's link, made once per link. */
  const draw = (invite: Invite): void => {
    if (drawn === invite.link) return;
    const link = invite.link;
    drawn = link;
    code.textContent = invite.code;
    path.setAttribute('d', '');
    void qrDrawing(link).then((qr) => {
      if (drawn !== link) return;
      const span = qr.size + QUIET * 2;
      svg.setAttribute(
        'viewBox',
        `${String(-QUIET)} ${String(-QUIET)} ${String(span)} ${String(span)}`,
      );
      path.setAttribute('d', qr.path);
    });
  };
  const hide = (): void => {
    beacon.hidden = true;
    at = '';
  };

  return {
    place(shown, pose, invite) {
      const hub = shown && invite ? shown.kind.indexOf(KIND.HUB) : -1;
      if (!shown || !invite || hub < 0) {
        hide();
        return;
      }
      draw(invite);
      dir.fromArray(shown.grid.centers, hub * 3);
      onPlanet(pose, dir, planet.tileTop(hub) + LIFT, point);
      const { x, y, behind, facing } = projector.project(point, -0.1, screen);
      if (!facing || behind || x < 0 || x > projector.width || y < 0 || y > projector.height) {
        hide();
        return;
      }
      beacon.hidden = false;
      // moved only when it lands on another pixel
      const px = Math.round(x);
      const py = Math.round(y);
      const where = `${String(px)},${String(py)}`;
      if (where === at) return;
      at = where;
      beacon.style.transform = `translate(${String(px)}px, ${String(py)}px) translate(-50%, -100%)`;
      beacon.style.setProperty('--y', `${String(py)}px`);
    },
    remove() {
      beacon.remove();
    },
  };
}
